/**
 * GraphExecutor — Interprets a graph directly (no compilation).
 *
 * Used for:
 * - Editor preview / live debugging
 * - Fallback when WASM compilation is unavailable
 * - Testing node behaviour
 *
 * Execution model:
 * 1. Find event nodes for the current lifecycle phase
 * 2. Walk flow edges from each event node
 * 3. For each impure node: evaluate data inputs (pull), execute, push flow
 * 4. Pure nodes are evaluated lazily when their output is needed
 *
 * Engine integration:
 * ```ts
 * const executor = new GraphExecutor(graph, registry, bridge);
 * executor.initialize(entityId);
 * // game loop:
 * executor.update(dt);
 * executor.fixedUpdate(fixedDt);
 * executor.lateUpdate(dt);
 * // cleanup:
 * executor.destroy();
 * ```
 */

import type {
  NodeBehaviour,
  NodeInstance,
  PortDataType,
  PortValue,
} from '../types';
import { Graph } from '../graph/base';
import { NodeRegistry } from '../node/NodeRegistry';
import { ExecutionContext, type EngineBridge } from './ExecutionContext';
import { defaultValueFor } from '../node/PortTypes';
import { Logger } from '@engine/logger';

// ============================================================================
// Executor state
// ============================================================================

export type ExecutorState = 'idle' | 'running' | 'paused' | 'destroyed';

// ============================================================================
// GraphExecutor
// ============================================================================

const log = Logger.create('GraphExecutor');

export class GraphExecutor {
  private readonly _context: ExecutionContext;
  private _state: ExecutorState = 'idle';
  private _frameCount = 0;
  /** How many frames to emit verbose logs (0 = off) */
  verboseFrames = 3;

  private get _verbose(): boolean {
    return this._frameCount <= this.verboseFrames;
  }

  constructor(
    private readonly _graph: Graph,
    private readonly _registry: NodeRegistry,
    bridge?: EngineBridge,
  ) {
    this._context = new ExecutionContext(bridge);
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  get state(): ExecutorState { return this._state; }
  get isInitialized(): boolean { return this._state !== 'idle'; }
  get isRunning(): boolean { return this._state === 'running'; }
  get frameCount(): number { return this._frameCount; }

  // --------------------------------------------------------------------------
  // Lifecycle — called by the engine
  // --------------------------------------------------------------------------

  /**
   * Initialize the graph instance: set up variables, fire awake → enable → start.
   * Call once when the entity/component is created.
   */
  initialize(entityId: number): void {
    if (this._state !== 'idle') return;

    this._context.entityId = entityId;
    this._context.initVariables(
      this._graph.getVariables().map(v => ({
        name: v.name,
        type: v.type,
        defaultValue: v.defaultValue,
      })),
    );
    this._state = 'running';

    const allNodes = this._graph.getNodes();
    const eventNodes = allNodes.filter(n => n.type.startsWith('events.'));
    log.debug(`initialize(entity=${entityId}): ${allNodes.length} nodes total, ${eventNodes.length} event nodes: [${eventNodes.map(n => n.type).join(', ')}]`);
    // Dump all node types for debugging
    for (const n of allNodes) {
      const def = this._registry.get(n.type);
      log.debug(`  node "${def?.name ?? '???'}" [${n.type}] hasExecute=${!!def?.execute} inputs=${JSON.stringify(n.inputValues)}`);
    }
    // Dump edges
    const allEdges = this._graph.getEdges();
    log.debug(`${allEdges.length} edge(s):`);
    for (const e of allEdges) {
      log.debug(`  edge: ${e.from.nodeId.slice(0,8)}:${e.from.portId} -> ${e.to.nodeId.slice(0,8)}:${e.to.portId}`);
    }

    this._executeEvent('events.awake');
    this._executeEvent('events.enable');
    this._executeEvent('events.start');
  }

  /**
   * Per-frame update. Call every frame from the game loop.
   */
  update(dt: number): void {
    if (this._state !== 'running') return;
    this._context.deltaTime = dt;
    this._context.time += dt;
    this._frameCount++;
    this._executeEvent('events.update');
  }

  /**
   * Fixed-timestep update. Call from physics step.
   */
  fixedUpdate(dt: number): void {
    if (this._state !== 'running') return;
    this._context.deltaTime = dt;
    this._executeEvent('events.fixedUpdate');
  }

  /**
   * Late update. Call after all updates.
   */
  lateUpdate(dt: number): void {
    if (this._state !== 'running') return;
    this._context.deltaTime = dt;
    this._executeEvent('events.late_update');
  }

  /**
   * Enable the executor (resume after pause).
   */
  enable(): void {
    if (this._state !== 'paused') return;
    this._state = 'running';
    this._executeEvent('events.enable');
  }

  /**
   * Disable / pause the executor. Stops update calls, fires onDisable.
   */
  disable(): void {
    if (this._state !== 'running') return;
    this._executeEvent('events.disable');
    this._state = 'paused';
  }

  /**
   * Destroy the executor. Fires onDisable + onDestroy. Not reusable after this.
   */
  destroy(): void {
    if (this._state === 'idle' || this._state === 'destroyed') return;
    if (this._state === 'running') {
      this._executeEvent('events.disable');
    }
    this._executeEvent('events.destroy');
    this._state = 'destroyed';
  }

  /**
   * Fire a custom event by type ID, optionally passing event-specific data.
   * Allows the engine to trigger graph-defined custom events.
   */
  triggerEvent(eventTypeId: string, params?: Record<string, PortValue>): void {
    if (this._state !== 'running') return;
    this._eventParams = params ?? null;
    this._executeEvent(eventTypeId);
    this._eventParams = null;
  }

  // --------------------------------------------------------------------------
  // Variable access — for engine/inspector integration
  // --------------------------------------------------------------------------

  /**
   * Get a graph variable's current value.
   */
  getVariable<T = PortValue>(name: string): T {
    return this._context.getVariable<T>(name);
  }

  /**
   * Set a graph variable's value (e.g. from inspector).
   */
  setVariable(name: string, value: PortValue): void {
    this._context.setVariable(name, value);
  }

  /**
   * Get all variable names and their current values.
   */
  getVariableSnapshot(): Record<string, PortValue> {
    const snap: Record<string, PortValue> = {};
    for (const v of this._graph.getVariables()) {
      snap[v.name] = this._context.getVariable(v.name);
    }
    return snap;
  }

  // --------------------------------------------------------------------------
  // Context access
  // --------------------------------------------------------------------------

  /**
   * Get the underlying execution context (for advanced introspection / debugging).
   */
  get context(): ExecutionContext {
    return this._context;
  }

  // --------------------------------------------------------------------------
  // Event execution
  // --------------------------------------------------------------------------

  private _loggedEvents = new Set<string>();
  /** Temporary event params passed via triggerEvent() */
  private _eventParams: Record<string, PortValue> | null = null;

  private _executeEvent(eventTypeId: string): void {
    const eventNodes = this._graph.getNodesByType(eventTypeId);

    // Log first occurrence of each event type, or always in verbose mode
    if (this._verbose || !this._loggedEvents.has(eventTypeId)) {
      this._loggedEvents.add(eventTypeId);
      log.debug(`_executeEvent("${eventTypeId}"): found ${eventNodes.length} node(s)`);
    }

    if (eventNodes.length === 0) return;

    this._context.clearPortCache();

    for (const eventNode of eventNodes) {
      const def = this._registry.get(eventNode.type);
      if (!def) {
        console.warn(`[GraphExecutor] No definition in registry for node type "${eventNode.type}"`);
        continue;
      }

      // Set event outputs (e.g. deltaTime for update)
      const eventOutputs: Record<string, unknown> = {};
      for (const outPort of def.outputs) {
        if (outPort.type === 'flow') continue;
        const value = this._getEventParam(eventTypeId, outPort.id);
        this._context.setPortOutput(eventNode.id, outPort.id, value);
        eventOutputs[outPort.id] = value;
      }
      if (this._verbose && Object.keys(eventOutputs).length > 0) {
        log.debug(`  event outputs:`, eventOutputs);
      }

      // Walk flow
      this._walkFlowFrom(eventNode);
    }
  }

  /**
   * Get an event parameter value.
   */
  private _getEventParam(eventTypeId: string, paramId: string): PortValue {
    // Check event-specific params first (collision/trigger data, etc.)
    if (this._eventParams && paramId in this._eventParams) {
      return this._eventParams[paramId];
    }
    if (paramId === 'dt' || paramId === 'deltaTime') return this._context.deltaTime;
    if (paramId === 'time') return this._context.time;
    if (paramId === 'entityId') return this._context.entityId;
    return null;
  }

  // --------------------------------------------------------------------------
  // Flow walking
  // --------------------------------------------------------------------------

  /**
   * Walk flow edges from a node, executing each node along the way.
   */
  private _walkFlowFrom(node: NodeInstance): void {
    const def = this._registry.get(node.type);
    if (!def) {
      log.warn(`_walkFlowFrom: no def for "${node.type}"`);
      return;
    }

    const flowOutputs = def.outputs.filter(p => p.type === 'flow');

    if (this._verbose) {
      const allEdges = flowOutputs.flatMap(fp => this._graph.getOutputEdges(node.id, fp.id));
      log.debug(`_walkFlowFrom("${node.type}"): ${flowOutputs.length} flow outputs, ${allEdges.length} edge(s)`);
    }

    for (const flowPort of flowOutputs) {
      const edges = this._graph.getOutputEdges(node.id, flowPort.id);
      for (const edge of edges) {
        const nextNode = this._graph.getNode(edge.to.nodeId);
        if (!nextNode) {
          if (this._verbose) log.debug(`  flow edge -> missing node ${edge.to.nodeId}`);
          continue;
        }
        if (this._verbose) {
          const nextDef = this._registry.get(nextNode.type);
          log.debug(`  flow: "${def.name}":${flowPort.id} -> "${nextDef?.name ?? nextNode.type}":${edge.to.portId}`);
        }
        this._executeNode(nextNode, edge.to.portId);
      }
    }
  }

  // --------------------------------------------------------------------------
  // Node execution
  // --------------------------------------------------------------------------

  /**
   * Execute a single node: resolve inputs, call execute, walk flow.
   */
  private _executeNode(node: NodeInstance, triggeredPort?: string): void {
    const def = this._registry.get(node.type) as NodeBehaviour | undefined;
    if (!def) {
      log.warn(`_executeNode: no def for "${node.type}"`);
      return;
    }

    const verbose = this._verbose;

    if (verbose) {
      log.debug(`exec "${def.name}" [${node.type}] hasExecute=${!!def.execute}${triggeredPort ? ` triggered=${triggeredPort}` : ''}`);
    }

    // Collect resolved inputs for logging
    const inputLog: Record<string, unknown> = {};

    // Create the execute context
    const ctx = this._context.createNodeContext(
      node.id,
      // Input resolver
      (portId: string) => {
        const val = this._resolveInputValue(node, portId);
        if (verbose) inputLog[portId] = val;
        return val;
      },
      // Flow trigger
      (portId: string) => {
        if (verbose) log.debug(`[GraphExecutor]   triggerFlow("${portId}")`);
        const edges = this._graph.getOutputEdges(node.id, portId);
        for (const edge of edges) {
          const next = this._graph.getNode(edge.to.nodeId);
          if (next) this._executeNode(next, edge.to.portId);
        }
      },
      triggeredPort,
    );

    // Execute
    if (def.execute) {
      try {
        def.execute(ctx);
      } catch (e) {
        log.fatal(`Error in node '${def.name}':`, e);
        this._context.bridge.log(`Error in node '${def.name}': ${e}`);
      }
    }

    if (verbose) {
      // Log resolved inputs
      const dataInputs = def.inputs.filter(p => p.type !== 'flow');
      if (dataInputs.length > 0) {
        // Force-resolve any lazy inputs that weren't pulled
        for (const ip of dataInputs) {
          if (!(ip.id in inputLog)) {
            inputLog[ip.id] = this._resolveInputValue(node, ip.id);
          }
        }
        log.debug(`  inputs:`, inputLog);
      }

      // Log outputs that were set
      const dataOutputs = def.outputs.filter(p => p.type !== 'flow');
      if (dataOutputs.length > 0) {
        const outputLog: Record<string, unknown> = {};
        for (const op of dataOutputs) {
          const v = this._context.getPortOutput(node.id, op.id);
          if (v !== undefined) outputLog[op.id] = v;
        }
        if (Object.keys(outputLog).length > 0) {
          log.debug(`  outputs:`, outputLog);
        }
      }
      console.groupEnd();
    }

    // Auto flow: if the node has exactly one flow output and didn't manually trigger,
    // walk it automatically
    const flowOuts = def.outputs.filter(p => p.type === 'flow');
    if (flowOuts.length === 1) {
      const edges = this._graph.getOutputEdges(node.id, flowOuts[0].id);
      for (const edge of edges) {
        const next = this._graph.getNode(edge.to.nodeId);
        if (next) this._executeNode(next, edge.to.portId);
      }
    }
  }

  // --------------------------------------------------------------------------
  // Input resolution (pull-based for data)
  // --------------------------------------------------------------------------

  /**
   * Resolve the value of a node's input port.
   */
  private _resolveInputValue(node: NodeInstance, portId: string): PortValue {
    const verbose = this._verbose;

    // Check for connected edge
    const edge = this._graph.getInputEdge(node.id, portId);
    if (edge) {
      // Check cache first
      const cached = this._context.getPortOutput(edge.from.nodeId, edge.from.portId);
      if (cached !== undefined) {
        if (verbose) {
          const srcDef = this._registry.get(this._graph.getNode(edge.from.nodeId)?.type ?? '');
          log.debug(`     resolve "${portId}": cached from "${srcDef?.name ?? edge.from.nodeId}".${edge.from.portId} =`, cached);
        }
        return cached;
      }

      // Evaluate the source node (recursively for pure nodes)
      const srcNode = this._graph.getNode(edge.from.nodeId);
      if (srcNode) {
        if (verbose) {
          const srcDef = this._registry.get(srcNode.type);
          log.debug(`     resolve "${portId}": pulling pure "${srcDef?.name ?? srcNode.type}".${edge.from.portId}`);
        }
        this._evaluatePureNode(srcNode);
        const result = this._context.getPortOutput(edge.from.nodeId, edge.from.portId);
        if (result !== undefined) return result;
      }
    }

    // Use inline value or default
    const def = this._registry.get(node.type);
    const portDef = def?.inputs.find(p => p.id === portId);
    const inlineValue = node.inputValues[portId];
    if (inlineValue !== undefined) {
      if (verbose) log.debug(`     resolve "${portId}": inline =`, inlineValue);
      return inlineValue as PortValue;
    }
    if (portDef?.defaultValue !== undefined) {
      if (verbose) log.debug(`     resolve "${portId}": default =`, portDef.defaultValue);
      return portDef.defaultValue as PortValue;
    }
    const fallback = defaultValueFor(portDef?.type ?? 'f32') as PortValue;
    if (verbose) log.debug(`     resolve "${portId}": type-default (${portDef?.type ?? 'f32'}) =`, fallback);
    return fallback;
  }

  /**
   * Evaluate a pure node on-demand (data pull).
   */
  private _evaluatePureNode(node: NodeInstance): void {
    const def = this._registry.get(node.type) as NodeBehaviour | undefined;
    if (!def?.execute) return;

    // Check if already evaluated
    const firstDataOut = def.outputs.find(p => p.type !== 'flow');
    if (firstDataOut && this._context.getPortOutput(node.id, firstDataOut.id) !== undefined) {
      return;
    }

    const verbose = this._verbose;
    if (verbose) log.debug(`eval pure "${def.name}" [${node.type}]"`);

    // Recursively evaluate inputs
    for (const inputPort of def.inputs) {
      if (inputPort.type === 'flow') continue;
      const edge = this._graph.getInputEdge(node.id, inputPort.id);
      if (edge) {
        const srcNode = this._graph.getNode(edge.from.nodeId);
        if (srcNode) this._evaluatePureNode(srcNode);
      }
    }

    // Execute
    const ctx = this._context.createNodeContext(
      node.id,
      (portId) => this._resolveInputValue(node, portId),
      () => {}, // pure nodes don't trigger flow
    );

    try {
      def.execute(ctx);
    } catch (e) {
      log.fatal(`Error in pure node '${def.name}':`, e);
      this._context.bridge.log(`Error in pure node '${def.name}': ${e}`);
    }

    if (verbose) {
      const dataOutputs = def.outputs.filter(p => p.type !== 'flow');
      const outputLog: Record<string, unknown> = {};
      for (const op of dataOutputs) {
        const v = this._context.getPortOutput(node.id, op.id);
        if (v !== undefined) outputLog[op.id] = v;
      }
      if (Object.keys(outputLog).length > 0) {
        log.debug(`  pure outputs:`, outputLog);
      }
      console.groupEnd();
    }
  }
}
